const HOST_PARTNER_MAP = [
  {
    pattern: 'aliancadobrasil.com.br',
    partnerId: 'aliancaDoBrasil',
  },
];

const DEFAULT_PARTNER = 'bb';
const STORAGE_KEY_PARTNER_ID = 'partner_id';
const STORAGE_KEY_PARTNER_HOST = 'partner_host';

/**
 * Extrai o parceiro a partir do hostname completo.
 *
 * Verifica se o primeiro segmento do hostname (antes do primeiro `.`)
 * corresponde a um prefixo conhecido no HOST_PARTNER_MAP.
 *
 * Hosts esperados em producao:
 * - `portal-parceiros-santander.cld.brasilseg.com.br` → "santander"
 * - `portal-parceiros.cld.brasilseg.com.br` → "bb"
 *
 * Retorna o partner_id correspondente ou o default "bb" quando:
 * - O hostname e null, vazio ou "localhost"
 * - O hostname nao mapeia para nenhum parceiro conhecido
 *
 * @param {string} hostname - O hostname completo (ex.: window.location.hostname)
 * @returns {string} O partner_id resolvido ("bb" ou "santander")
 */
export const extractPartnerFromHost = (hostname) => {
  if (!hostname) {
    return DEFAULT_PARTNER;
  }

  if (hostname === 'localhost') {
    const partnerID = process.env.REACT_APP_PARTNER;
    return partnerID || DEFAULT_PARTNER;
  }

  const prefix = hostname.split('.')[0];
  const domain = hostname.split('.').slice(-3).join('.');
  const match = HOST_PARTNER_MAP.find((entry) =>
    [prefix, domain]?.includes(entry.pattern),
  );

  return match ? match.partnerId : DEFAULT_PARTNER;
};

/**
 * Verifica se o hostname e um host conhecido (mapeavel para parceiro).
 *
 * Diferencia hosts de producao (que mapeiam para parceiro real) de hosts
 * desconhecidos (localhost, ambientes de dev) onde `extractPartnerFromHost`
 * retornaria "bb" como fallback — nao porque e o host BB real.
 *
 * @param {string} hostname - O hostname completo (ex.: window.location.hostname)
 * @returns {boolean} true se o hostname e conhecido, false caso contrario
 */
const isKnownHost = (hostname) => {
  if (!hostname) {
    return false;
  }

  const prefix = hostname.split('.')[0];
  const domain = hostname.split('.').slice(-3).join('.');

  // Host BB de producao (security sem sufixo de parceiro)
  const hosts = ['localhost', 'aliancadobrasil.com.br'];
  if (hosts.includes(prefix) || hosts.includes(domain)) {
    return true;
  }

  // Host de parceiro mapeado no HOST_PARTNER_MAP
  const match = HOST_PARTNER_MAP.find((entry) =>
    [prefix, domain]?.includes(entry.pattern),
  );
  return !!match;
};

/**
 * Resolve o parceiro a partir do hostname atual (resolucao global).
 *
 * Fluxo de resolucao:
 * 1. Obtem hostname atual via `window.location.hostname`
 * 2. Extrai parceiro via `extractPartnerFromHost(currentHost)`
 * 3. Verifica se hostname e conhecido via `isKnownHost(currentHost)`
 * 4. SE hostname e conhecido → usa parceiro extraido, grava no sessionStorage, retorna
 * 5. SE hostname NAO e conhecido (localhost, desconhecido) → le `partner_id` do sessionStorage como fallback
 * 6. SE sessionStorage vazio → retorna "bb" como fallback final
 *
 * Otimizacao: se `partner_host` no sessionStorage === hostname atual E `partner_id`
 * ja esta correto, reutiliza sem re-escrita no sessionStorage.
 *
 * @returns {string} O partner_id resolvido
 */
export const resolvePartnerByHost = () => {
  const currentHost = window.location.hostname;
  const storedHost = window.sessionStorage.getItem(STORAGE_KEY_PARTNER_HOST);
  const storedPartnerId = window.sessionStorage.getItem(STORAGE_KEY_PARTNER_ID);
  const partnerId = extractPartnerFromHost(currentHost);

  if (isKnownHost(currentHost)) {
    // Otimizacao: se host nao mudou e partner_id ja esta correto, reutilizar sem re-escrita
    if (storedHost === currentHost && storedPartnerId === partnerId) {
      return storedPartnerId;
    }

    // Host conhecido — gravar no sessionStorage e retornar
    window.sessionStorage.setItem(STORAGE_KEY_PARTNER_ID, partnerId);
    window.sessionStorage.setItem(STORAGE_KEY_PARTNER_HOST, currentHost);
    return partnerId;
  }

  // Host NAO conhecido (localhost, desconhecido) — fallback sessionStorage
  if (storedPartnerId) {
    return storedPartnerId;
  }

  // Fallback final — retornar "bb"
  return DEFAULT_PARTNER;
};

/**
 * Le o partner_id do sessionStorage para rotas pos-login.
 *
 * Retorna o partner_id armazenado ou "bb" como fallback quando
 * o sessionStorage esta vazio (ex.: aba nova, sessao expirada).
 *
 * @returns {string} O partner_id armazenado ou "bb" como fallback
 */
export const getStoredPartner = () =>
  window.sessionStorage.getItem(STORAGE_KEY_PARTNER_ID) || DEFAULT_PARTNER;

/**
 * Remove partner_id e partner_host do sessionStorage.
 *
 * Deve ser chamado no logout para que o proximo acesso ao /v2/login
 * refaca a deteccao de host do zero.
 */
export const clearStoredPartner = () => {
  window.sessionStorage.removeItem(STORAGE_KEY_PARTNER_ID);
  window.sessionStorage.removeItem(STORAGE_KEY_PARTNER_HOST);
};

/**
 * Compara o hostname atual com o partner_host armazenado no sessionStorage.
 *
 * Retorna true se houve troca de host (hostname atual difere do armazenado),
 * indicando que o parceiro deve ser re-resolvido.
 *
 * @returns {boolean} true se o host mudou, false caso contrario
 */
export const detectHostChange = () => {
  const storedHost = window.sessionStorage.getItem(STORAGE_KEY_PARTNER_HOST);

  // Se nao ha host armazenado, considerar como "mudanca" (primeira visita)
  if (!storedHost) {
    return true;
  }

  return window.location.hostname !== storedHost;
};
